import { useState, useRef } from 'react';
import { getEnv, getRequiredEnv, t } from '../../utils/helpers';
import { container } from 'tsyringe';
import { ModalObservable } from '../../features/game/model/interfaces';
import { useClickOutside } from '@utils/hooks';
import { toggleFullScreen } from '@utils/functions';
import ContactUs from '@base/features/contactUS/contactUs';
import { useFacebookLogin } from '@base/features/auth/hooks/facebookLogin';
import { useGoogleAuth } from '@base/features/auth/hooks/googleLogin';
import { ReactComponent as PreferencesIcon } from '@base/assets/images/icon_preferen.svg';
import { ReactComponent as FullScreenIcon } from '@base/assets/images/icon_fullscreen.svg';
import { ReactComponent as MoreGamesIcon } from '@base/assets/images/icon_moregames.svg';
import { ReactComponent as ContactUsIcon } from '@base/assets/images/icon_contactus.svg';
import { ReactComponent as LogoutIcon } from '@base/assets/images/icon_logout.svg';
import { ReactComponent as ExitFullScreenIcon } from '@base/assets/images/icon_exitfull.svg';
import styles from '../styles/burger.module.scss';
import MoreGamesModal from '@base/features/moreGames/ui/moreGamesModal';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import LobbyService from '@base/features/lobby/model/lobbyService';
import preferencesModal from '@base/features/preferences/components/preferencesModal';

function Burger(props: { currentUser?: string }) {
  const modalProvider: IModalProvider = container.resolve('modalProvider');
  const lobbyService: LobbyService = container.resolve('lobbyService');
  const [showMenuBurger, setShowMenuBurger] = useState(false);
  const { logout: logoutFacebook } = useFacebookLogin();
  const { logoutGoogle } = useGoogleAuth();

  const ref = useRef(null);
  useClickOutside(ref, () => setShowMenuBurger(false));

  async function handleLogout() {
    logoutFacebook();
    logoutGoogle();
    lobbyService.logout();
    modalProvider.hide();
  }

  const logoutModal: ModalObservable = {
    body: t('logout_prompt'),
    buttons: [
      { title: t('cancel'), onClick: () => modalProvider.hide() },
      { title: t('logout'), onClick: () => handleLogout() },
    ],
  };

  return (
    <div className={styles.burger} ref={ref}>
      <div
        className={!showMenuBurger ? styles.burgerMenu : styles.burgerMenu__active}
        onClick={() => {
          setShowMenuBurger((prevState) => !prevState);
        }}
      >
        <span />
      </div>
      <nav className={!showMenuBurger ? styles.list__hide : styles.list}>
        <ul>
          <li
            className={styles.item}
            onClick={() => {
              modalProvider.show(preferencesModal());
              setShowMenuBurger(false);
            }}
          >
            <PreferencesIcon /> {t('preferences')}
          </li>
          <li
            className={styles.item}
            onClick={() => {
              toggleFullScreen();
              setShowMenuBurger(false);
            }}
          >
            {document.fullscreenElement ? <ExitFullScreenIcon width={24} height={24} /> : <FullScreenIcon />}
            {document.fullscreenElement ? t('exit_full_screen') : t('full_screen')}
          </li>
          <li
            className={styles.item}
            onClick={() => {
              modalProvider.show({
                title: t('more_games'),
                body: <MoreGamesModal />,
                classes: { modal: styles.moreGames, body: styles.body, title: styles.title },
                headerBtn: true,
                closeModal: () => modalProvider.hide(),
              });
              setShowMenuBurger(false);
            }}
          >
            <MoreGamesIcon /> {t('more_games')}
          </li>

          <li
            className={styles.item}
            onClick={() => {
              modalProvider.show({
                title: t('contact_us'),
                body: <ContactUs />,
                headerBtn: true,
                closeModal: () => modalProvider.hide(),
              });
              setShowMenuBurger(false);
            }}
          >
            <ContactUsIcon /> {t('contact_us')}
          </li>

          {props.currentUser && (
            <li
              className={styles.item}
              onClick={() => {
                modalProvider.show(logoutModal);
                setShowMenuBurger(false);
              }}
            >
              <LogoutIcon /> {`${t('logout')} ${props.currentUser}`}
            </li>
          )}
          <li className={styles.item} style={{marginLeft: '10px'}}>
            <span>Version: </span>
            <span>{getRequiredEnv('NX_VERSION_TAG')}</span>
            <span>{getEnv('NX_BUILD_INFO')}</span>
          </li>
        </ul>
      </nav>
    </div>
  );
}

export default Burger;
